import { ScreenClassProvider, SWAN_STYLE_KEY_MAP } from '@vp/swan'
import { useSwanStyleKeys, useUserContextTracker, useLogger } from '@vp/ubik-context'
import { ContentfulContextProvider, DebugSection, ContentfulLink } from '@vp/mcs-react-tools'
import { CampaignProvider } from '@vp/campaign-callouts'
import { ProductCategoriesContainer } from './ProductCategoriesContainer'
import type { CategoriesRowProps } from '../../types/homepageCategories'
import { TrackingProvider } from '../../hooks/trackingContext'
import { FragmentLogger } from '@vp/ubik-logging'
import type { TrackUsedContextProps } from '../../types/sharedTypes'

export const HomepageCategories = (props: CategoriesRowProps) => {
  useSwanStyleKeys([SWAN_STYLE_KEY_MAP.core, SWAN_STYLE_KEY_MAP.grid, SWAN_STYLE_KEY_MAP.carousel, SWAN_STYLE_KEY_MAP.standardTile, SWAN_STYLE_KEY_MAP.utility])
  const logger = useLogger() as FragmentLogger

  logger.setContext({
    ...props,
    name: 'category-row-fragment',
  })

  logger.info('Rendering Category Row Fragment')
  const data = {
    pageData: props.pageData,
    merchandisingCampaign: props.merchandisingCampaign,
    useNewTiles: props.pageData.useNewTiles,
    carouselA11yText: props.carouselA11yText,
    locale: props.locale,
    tenant: props.tenant,
    debugData: {
      experimentKey: props.experimentKey,
      variationKey: props.variationKey,
      decision: props.decision,
      contentfulMetadata: props.contentfulMetadata,
    },
  }

  const search = typeof window !== 'undefined' ? window.location.search : ''
  trackUsedContext(props)

  const debugAvailability = {
    merchandisingCampaign: props.merchandisingCampaign,
    experimentKey: props.experimentKey,
    variationKey: props.variationKey,
    decision: props.decision,
  }

  return (
    <ContentfulContextProvider space={props.contentfulMetadata.spaceId}>
      <ContentfulLink id={props.contentfulMetadata?.contentful_id} />
      <DebugSection contentfulMetadata={props.contentfulMetadata} availability={debugAvailability} search={search} />
      <TrackingProvider canonicalUrl="https://www.vistaprint.com/">
        <CampaignProvider tenant={props.tenant} locale={props.locale}>
          <ScreenClassProvider>
            <ProductCategoriesContainer data={data} imageLoadingStrategy="eager" />
          </ScreenClassProvider>
        </CampaignProvider>
      </TrackingProvider>
    </ContentfulContextProvider>
  )
}

function trackUsedContext(props: TrackUsedContextProps) {
  const tracker = useUserContextTracker()
  if (props.experimentKey) {
    tracker({
      type: 'experiment',
      name: props.experimentKey,
      variation: props.variationKey,
    })
  }
  if (props.decision) {
    tracker({
      type: 'treatment',
      name: props.decision,
    })
  }
  if (props.merchandisingCampaign) {
    tracker({
      type: 'campaign',
      name: props.merchandisingCampaign,
    })
  }
}
